import { createContext, useContext, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { authAPI } from './api'

const AuthContext = createContext()

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token')
      const savedUser = localStorage.getItem('user')

      if (token && savedUser) {
        try {
          // Verify token is still valid
          const response = await authAPI.getProfile()
          const userData = response.data.user
          
          setUser(userData)
          setIsAuthenticated(true)
        } catch (error) {
          // Token is invalid, clear storage
          localStorage.removeItem('token')
          localStorage.removeItem('user')
          setUser(null)
          setIsAuthenticated(false)
        }
      }
      
      setLoading(false)
    }

    initializeAuth()
  }, [])

  const login = async (credentials) => {
    try {
      setLoading(true)
      
      // Demo mode - hardcoded credentials when backend is not available
      const demoUsers = {
        'george': { password: 'george123', user: { id: 1, username: 'george', email: 'george@cybersafe.com', full_name: 'George Admin', is_admin: true } },
        'leeroy': { password: 'leeroy123', user: { id: 2, username: 'leeroy', email: 'leeroy@cybersafe.com', full_name: 'Leeroy User', is_admin: false } }
      }
      
      // Check if using demo credentials
      const demoUser = demoUsers[credentials.username]
      if (demoUser && demoUser.password === credentials.password) {
        // Demo login successful
        const demoToken = 'demo-token-' + Math.random().toString(36).substring(7)
        localStorage.setItem('token', demoToken)
        localStorage.setItem('user', JSON.stringify(demoUser.user))
        
        setUser(demoUser.user)
        setIsAuthenticated(true)
        
        toast.success('Login successful!')
        return { success: true, user: demoUser.user }
      }
      
      // Get user location before login
      const getUserLocation = () => {
        return new Promise((resolve) => {
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                resolve({
                  latitude: position.coords.latitude,
                  longitude: position.coords.longitude
                });
              },
              () => {
                resolve(null); // Location access denied
              }
            );
          } else {
            resolve(null); // Geolocation not supported
          }
        });
      };

      const location = await getUserLocation();
      const loginData = { ...credentials };
      if (location) {
        loginData.location = location;
      }

      const response = await authAPI.login(loginData)
      const { access_token, user: userData } = response.data

      // Store token and user data
      localStorage.setItem('token', access_token)
      localStorage.setItem('user', JSON.stringify(userData))

      setUser(userData)
      setIsAuthenticated(true)
      
      toast.success('Login successful!')
      return { success: true, user: userData }
    } catch (error) {
      const message = error.response?.data?.error || 'Login failed. Please check your credentials.'
      toast.error(message)
      return { success: false, error: message }
    } finally {
      setLoading(false)
    }
  }

  const register = async (userData) => {
    try {
      setLoading(true)
      const response = await authAPI.register(userData)
      const { access_token, user_id } = response.data

      // Store token
      localStorage.setItem('token', access_token)

      // Get user profile to get complete user data
      const profileResponse = await authAPI.getProfile()
      const userProfile = profileResponse.data.user

      localStorage.setItem('user', JSON.stringify(userProfile))
      setUser(userProfile)
      setIsAuthenticated(true)
      
      toast.success('Registration successful!')
      return { success: true, user: userProfile }
    } catch (error) {
      const message = error.response?.data?.error || 'Registration failed'
      toast.error(message)
      return { success: false, error: message }
    } finally {
      setLoading(false)
    }
  }

  const logout = async () => {
    try {
      await authAPI.logout()
    } catch (error) {
      // Even if logout fails on server, we should clear local storage
      console.error('Logout error:', error)
    } finally {
      // Clear local storage and state
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      setUser(null)
      setIsAuthenticated(false)
      toast.success('Logged out successfully')
    }
  }

  const updateProfile = async (profileData) => {
    try {
      await authAPI.updateProfile(profileData)
      
      // Refresh user data
      const response = await authAPI.getProfile()
      const updatedUser = response.data.user
      
      setUser(updatedUser)
      localStorage.setItem('user', JSON.stringify(updatedUser))
      
      toast.success('Profile updated successfully!')
      return { success: true, user: updatedUser }
    } catch (error) {
      const message = error.response?.data?.error || 'Profile update failed'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  const changePassword = async (passwordData) => {
    try {
      await authAPI.changePassword(passwordData)
      toast.success('Password changed successfully!')
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.error || 'Password change failed'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  const enrollBiometric = async (biometricData) => {
    try {
      await authAPI.enrollBiometric(biometricData)
      toast.success('Biometric data enrolled successfully!')
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.error || 'Biometric enrollment failed'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    enrollBiometric,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext